/**
 * Custom Edge with Add Button
 * Shows a + button in the middle of the edge to add new actions
 */

import { memo, useCallback } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
} from '@xyflow/react';

interface AddButtonEdgeProps extends EdgeProps {
  data?: {
    tipo?: string;
    label?: string;
    onAddClick?: (sourceId: string, targetId: string, position: { x: number; y: number }) => void;
  };
}

function AddButtonEdge({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
  source,
  target,
}: AddButtonEdgeProps) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const handleAddClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();

    // Dispatch custom event to trigger add action menu
    const event = new CustomEvent('flow-add-action', {
      detail: {
        sourceId: source,
        targetId: target,
        position: { x: labelX, y: labelY },
      },
    });
    window.dispatchEvent(event);
  }, [source, target, labelX, labelY]);

  // Don't show add button on jump edges or finish edges
  const isJumpEdge = data?.tipo === 'salto' || data?.tipo === 'alternativa';
  const isFinishEdge = data?.tipo === 'fin';
  const showAddButton = !isJumpEdge && !isFinishEdge && source !== 'INICIO';

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={style}
      />
      {showAddButton && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
              zIndex: 1000,
            }}
            className="nodrag nopan"
          >
            <button
              onClick={handleAddClick}
              className="edge-add-button"
              title="Agregar acción aquí"
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: '#fff',
                border: '2px solid #d1d5db',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                opacity: 0.6,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.background = '#10b981';
                e.currentTarget.style.borderColor = '#10b981';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '0.6';
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                style={{ color: '#6b7280' }}
                className="edge-add-icon"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export default memo(AddButtonEdge);
